import dayjs, { Dayjs } from 'dayjs'

import {
  InvestigationsService,
  ListSortDirection,
  SearchSpecimenSortBy,
  SpecimenLaboratoryStatus
} from 'src/api/requests'

export type ReceptionValues = {
  receivedAt?: Dayjs
  isConform?: boolean
  nonConformityComment?: string
}

/** Newest specimen of a person. The external result endpoint answers with an
 *  empty 200, so the specimen it builds in the background can only be spotted
 *  by comparing this before and after the call. Replace with the id returned by
 *  the endpoint once the backend exposes it. */
const getLatestSpecimenId = async (personId: string) => {
  const response = await InvestigationsService.getListSpecimensEndpoint(
    1,
    1,
    null,
    ListSortDirection.DESCENDING,
    SearchSpecimenSortBy.CREATED_AT,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    [personId]
  )

  return response?.data?.[0]?.id
}

/** Reception date and conformity are not part of the external result contract,
 *  so they are written on the generated specimen through the regular laboratory
 *  reception endpoints. */
export const useExternalResultReception = () => {
  const captureLatestSpecimenId = (personId: string) =>
    getLatestSpecimenId(personId).catch(() => undefined)

  const resolveCreatedSpecimenId = async (
    personId: string,
    previousSpecimenId?: string
  ) => {
    const latestSpecimenId = await getLatestSpecimenId(personId).catch(
      () => undefined
    )

    // A specimen that was already the newest one before the call is somebody
    // else's; receipting it would attach this reception to the wrong sample.
    return latestSpecimenId && latestSpecimenId !== previousSpecimenId
      ? latestSpecimenId
      : undefined
  }

  const applyReception = async (
    specimenId: string,
    values: ReceptionValues
  ) => {
    const comment = values.isConform
      ? undefined
      : values.nonConformityComment || undefined

    await InvestigationsService.specimenReceptionEndpoint(specimenId, {
      receivedAt: dayjs(values.receivedAt).format(),
      comments: comment
    })

    await InvestigationsService.updateSpecimenEndpoint(specimenId, {
      laboratoryStatus: values.isConform
        ? SpecimenLaboratoryStatus.VALIDATED
        : SpecimenLaboratoryStatus.INCONFORM,
      comment
    })
  }

  return { captureLatestSpecimenId, resolveCreatedSpecimenId, applyReception }
}
